/**
 * ForecastExportModal
 *
 * Modal dialog for configuring and triggering a forecast data export.
 * Users select a single weather model, then one-or-more impact models
 * (filtered by compatibility), variables (intersection of selected models),
 * operating companies, date, run, and format.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { trackRumAction } from "@/lib/datadog";
import { format, startOfDay, parseISO, isAfter, isValid } from "date-fns";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, ChevronDown, ChevronRight, MapPinIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ModelRunSelector } from "@/components/shared/nav/selectors/ModelRunSelector";
import { useForecastExport, useAvailableRuns } from "@/hooks/api";
import type {
  StormImpactModel,
  WeatherModel,
  GeographicNode,
  GeographicHierarchy,
} from "@/types";
import type { ExportFormat, ForecastExportRequest } from "@/api/types";

interface ForecastExportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** All storm impact models available */
  stormImpactModels: StormImpactModel[];
  /** Geographic hierarchy for node selection */
  geographicHierarchy: GeographicHierarchy | null;
  /** Currently selected date on the dashboard (initial value) */
  initialDate?: Date;
  /** Currently selected run on the dashboard (initial value) */
  initialRun?: Date;
  /** Available runs for the selected models */
  availableRuns?: string[];
  /** Dashboard's selected weather model ID for pre-selection */
  initialWeatherModelId?: string;
  /** Dashboard's selected impact model ID for pre-selection */
  initialStormImpactModelId?: string;
  /** Optional: hurricane storm ID */
  stormId?: string;
  /** Optional: hurricane track snapshot ID */
  trackSnapshotId?: string;
}

interface VariableOption {
  slug: string;
  name: string;
  units: string;
}

interface FlattenedGeoNode {
  id: string;
  name: string;
  resolution: string;
  indent: number;
  selectable: boolean;
}

function CollapsibleGeoNode({
  node,
  depth,
  selectedIds,
  expandedIds,
  disabledIds,
  descendantIdsMap,
  onToggleSelect,
  onToggleExpand,
}: Readonly<{
  node: GeographicNode;
  depth: number;
  selectedIds: Set<string>;
  expandedIds: Set<string>;
  disabledIds: Set<string>;
  descendantIdsMap: Map<string, string[]>;
  onToggleSelect: (id: string) => void;
  onToggleExpand: (id: string) => void;
}>) {
  const hasChildren = node.trees && node.trees.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedIds.has(node.id);
  const isDisabled = !node.selectable || disabledIds.has(node.id);

  const isIndeterminate = useMemo(() => {
    if (isSelected || !hasChildren) return false;
    const descendants = descendantIdsMap.get(node.id) ?? [];
    return descendants.some((id) => selectedIds.has(id));
  }, [isSelected, hasChildren, node.id, selectedIds, descendantIdsMap]);

  const checkedState: boolean | "indeterminate" = isSelected
    ? true
    : isIndeterminate
      ? "indeterminate"
      : false;

  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-1 px-2 py-2 text-sm rounded-sm",
          isDisabled
            ? "opacity-50 cursor-not-allowed text-muted-foreground"
            : "hover:bg-accent hover:text-accent-foreground cursor-pointer",
        )}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="p-0 h-4 w-4 shrink-0 flex items-center justify-center hover:text-foreground text-muted-foreground"
            onClick={(e) => {
              e.preventDefault();
              onToggleExpand(node.id);
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <label className={cn("flex items-center gap-2", isDisabled ? "cursor-not-allowed" : "cursor-pointer")}>
          <Checkbox
            checked={checkedState}
            onCheckedChange={
              isDisabled ? undefined : () => onToggleSelect(node.id)
            }
            disabled={isDisabled}
          />
          <span>{node.name}</span>
          <span className="text-muted-foreground text-xs ml-1">
            ({node.resolution})
          </span>
        </label>
      </div>
      {hasChildren && isExpanded && (
        <div>
          {node.trees.map((child) => (
            <CollapsibleGeoNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedIds={selectedIds}
              expandedIds={expandedIds}
              disabledIds={disabledIds}
              descendantIdsMap={descendantIdsMap}
              onToggleSelect={onToggleSelect}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Collect all unique weather models across all impact models.
 */
function getAllWeatherModels(
  stormImpactModels: StormImpactModel[],
): WeatherModel[] {
  const seen = new Map<string, WeatherModel>();
  for (const im of stormImpactModels) {
    for (const wm of im.weather_models ?? []) {
      if (!seen.has(wm.id)) {
        seen.set(wm.id, wm);
      }
    }
  }
  return Array.from(seen.values());
}

/**
 * Get impact models that are compatible with a given weather model.
 */
function getCompatibleImpactModels(
  stormImpactModels: StormImpactModel[],
  weatherModelId: string,
): StormImpactModel[] {
  return stormImpactModels.filter((im) =>
    im.weather_models?.some((wm) => wm.id === weatherModelId),
  );
}

/**
 * Get intersection of variables across selected impact models.
 */
function getIntersectedVariables(
  stormImpactModels: StormImpactModel[],
  selectedImpactModelIds: string[],
): VariableOption[] {
  if (selectedImpactModelIds.length === 0) return [];

  const selectedModels = selectedImpactModelIds
    .map((id) => stormImpactModels.find((m) => m.model_id === id))
    .filter(Boolean) as StormImpactModel[];

  if (selectedModels.length === 0) return [];

  const firstVars = selectedModels[0].variables ?? [];
  const intersected = firstVars.filter((v) =>
    selectedModels.every((m) =>
      m.variables?.some((mv) => mv.variable_slug === v.variable_slug),
    ),
  );

  return intersected.map((v) => ({
    slug: v.variable_slug,
    name: v.variable_name,
    units: v.variable_units,
  }));
}

export function ForecastExportModal({
  open,
  onOpenChange,
  stormImpactModels,
  geographicHierarchy,
  initialDate,
  initialRun,
  availableRuns = [],
  initialWeatherModelId,
  initialStormImpactModelId,
  stormId,
  trackSnapshotId,
}: Readonly<ForecastExportModalProps>) {
  // ============================================================================
  // Export hook
  // ============================================================================
  const { exportForecasts, isExporting, error, reset } = useForecastExport();

  // ============================================================================
  // State
  // ============================================================================
  const [selectedWeatherModelId, setSelectedWeatherModelId] = useState<
    string | null
  >(null);
  const [selectedImpactModelIds, setSelectedImpactModelIds] = useState<
    string[]
  >([]);
  const [selectedVariableSlugs, setSelectedVariableSlugs] = useState<string[]>(
    [],
  );
  const [selectedOperatingCompanyIds, setSelectedOperatingCompanyIds] = useState<
    string[]
  >([]);
  const [selectedDate, setSelectedDate] = useState<Date>(
    initialDate || new Date(),
  );
  const [selectedRun, setSelectedRun] = useState<Date | undefined>(initialRun);
  const [exportFormat, setExportFormat] = useState<ExportFormat>("excel");
  const selectedVariableSlugsRef = useRef(selectedVariableSlugs);
  selectedVariableSlugsRef.current = selectedVariableSlugs;
  const [inlineAlert, setInlineAlert] = useState<string | null>(null);
  const inlineAlertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [expandedGeoNodeIds, setExpandedGeoNodeIds] = useState<Set<string>>(
    new Set(),
  );
  const [weatherModelPopoverOpen, setWeatherModelPopoverOpen] = useState(false);

  // ============================================================================
  // Derived data
  // ============================================================================

  // All unique weather models across all impact models
  const allWeatherModels = useMemo(
    () => getAllWeatherModels(stormImpactModels),
    [stormImpactModels],
  );

  // Impact models compatible with the selected weather model
  const compatibleImpactModels = useMemo(() => {
    if (!selectedWeatherModelId) return [];
    return getCompatibleImpactModels(stormImpactModels, selectedWeatherModelId);
  }, [stormImpactModels, selectedWeatherModelId]);

  // Intersection of variables from selected impact models
  const availableVariables = useMemo(
    () => getIntersectedVariables(stormImpactModels, selectedImpactModelIds),
    [stormImpactModels, selectedImpactModelIds],
  );

  // Single tree walk: builds descendant map, direct-child map, parent map, and flattened nodes
  const { descendantIdsMap, childIdsMap, parentIdMap, flattenedGeoNodes } = useMemo(() => {
    const descMap = new Map<string, string[]>();
    const childMap = new Map<string, string[]>();
    const parMap = new Map<string, string>();
    const flat: FlattenedGeoNode[] = [];

    function collect(node: GeographicNode, parentId: string | undefined, depth: number): string[] {
      if (parentId) parMap.set(node.id, parentId);
      flat.push({
        id: node.id,
        name: node.name,
        resolution: node.resolution,
        indent: depth,
        selectable: node.selectable,
      });
      const children = node.trees ?? [];
      childMap.set(node.id, children.map((c) => c.id));
      const ids: string[] = [];
      for (const child of children) {
        ids.push(child.id, ...collect(child, node.id, depth + 1));
      }
      descMap.set(node.id, ids);
      return ids;
    }

    for (const tree of geographicHierarchy?.trees ?? []) {
      collect(tree, undefined, 0);
    }
    return { descendantIdsMap: descMap, childIdsMap: childMap, parentIdMap: parMap, flattenedGeoNodes: flat };
  }, [geographicHierarchy]);

  const selectedIdsSet = useMemo(
    () => new Set(selectedOperatingCompanyIds),
    [selectedOperatingCompanyIds],
  );

  // Nodes that are grandchildren (depth >= 2) of any selected node are uncheckable.
  const disabledGeoNodeIds = useMemo(() => {
    const disabled = new Set<string>();
    const selectedSet = new Set(selectedOperatingCompanyIds);
    if (selectedSet.size === 0) return disabled;

    for (const node of flattenedGeoNodes) {
      let current = node.id;
      let distance = 0;
      while (parentIdMap.has(current)) {
        current = parentIdMap.get(current)!;
        distance++;
        if (distance >= 2 && selectedSet.has(current)) {
          disabled.add(node.id);
          break;
        }
      }
    }
    return disabled;
  }, [selectedOperatingCompanyIds, flattenedGeoNodes, parentIdMap]);

  // Get max forecast date from selected weather model
  const maxForecastDate = useMemo(() => {
    if (!selectedWeatherModelId) return undefined;
    const wm = allWeatherModels.find((w) => w.id === selectedWeatherModelId);
    if (!wm?.max_forecast_date) return undefined;
    return new Date(wm.max_forecast_date);
  }, [selectedWeatherModelId, allWeatherModels]);

  const runFetchParams = useMemo(() => {
    if (selectedImpactModelIds.length === 0 || !selectedWeatherModelId) return null;
    // Sort IDs so selection order doesn't partition the react-query cache.
    const sortedModelIds = [...selectedImpactModelIds].sort();
    return {
      stormImpactModelId: sortedModelIds.join(","),
      weatherModelId: selectedWeatherModelId,
      forecastDate: format(selectedDate, "yyyy-MM-dd"),
      isExport: true,
    };
  }, [
    selectedImpactModelIds,
    selectedWeatherModelId,
    selectedDate,
  ]);

  // Fetch available runs
  const { data: modalAvailableRunsData, loading: runsLoading } =
    useAvailableRuns(runFetchParams ?? {});

  const availableRunsForSelector: string[] = useMemo(() => {
    if (!runFetchParams) return [];
    if (modalAvailableRunsData) {
      return modalAvailableRunsData.map((r) => r.run);
    }
    return [];
  }, [runFetchParams, modalAvailableRunsData]);

  const isRunSelectorLoading = Boolean(runFetchParams && runsLoading);

  const runHourMap: Record<string, string> = useMemo(() => {
    if (!modalAvailableRunsData) return {};
    const map: Record<string, string> = {};
    for (const r of modalAvailableRunsData) {
      if (r.run_hour) map[r.run] = r.run_hour;
    }
    return map;
  }, [modalAvailableRunsData]);

  // Preselect latest run when available runs change
  useEffect(() => {
    if (!availableRunsForSelector.length) {
      setSelectedRun(undefined);
      return;
    }
    const sorted = [...availableRunsForSelector].sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    );
    const latestRun = sorted[0];
    if (selectedRun) {
      const selectedTime = selectedRun.getTime();
      const stillAvailable = availableRunsForSelector.some(
        (r) => new Date(r).getTime() === selectedTime,
      );
      if (stillAvailable) return;
    }
    setSelectedRun(new Date(latestRun));
  }, [availableRunsForSelector, selectedRun]);

  // ============================================================================
  // Inline alert (renders inside modal, no z-index issues)
  // ============================================================================
  const showInlineAlert = useCallback((message: string) => {
    if (inlineAlertTimerRef.current) clearTimeout(inlineAlertTimerRef.current);
    setInlineAlert(message);
    inlineAlertTimerRef.current = setTimeout(() => setInlineAlert(null), 4000);
  }, []);

  // When available variables change, prune selected slugs that are no longer available
  useEffect(() => {
    const currentSlugs = selectedVariableSlugsRef.current;
    if (currentSlugs.length === 0) return;

    const availableSlugs = new Set(availableVariables.map((v) => v.slug));
    const removedSlugs = currentSlugs.filter((s) => !availableSlugs.has(s));
    if (removedSlugs.length === 0) return;

    const removedNames = removedSlugs
      .map(
        (s) =>
          stormImpactModels
            .flatMap((m) => m.variables ?? [])
            .find((v) => v.variable_slug === s)?.variable_name ?? s,
      )
      .join(", ");

    setSelectedVariableSlugs((prev) =>
      prev.filter((s) => availableSlugs.has(s)),
    );

    showInlineAlert(
      `${removedNames} ${removedSlugs.length === 1 ? "is" : "are"} not available for the selected impact ${selectedImpactModelIds.length === 1 ? "model" : "models"} and ${removedSlugs.length === 1 ? "has" : "have"} been deselected.`,
    );
  }, [availableVariables, showInlineAlert]);

  // ============================================================================
  // Initialize selections when modal opens
  // ============================================================================
  useEffect(() => {
    if (open) {
      const wmId = initialWeatherModelId ?? null;
      setSelectedWeatherModelId(wmId);
      setSelectedImpactModelIds(
        initialStormImpactModelId ? [initialStormImpactModelId] : [],
      );
      setSelectedVariableSlugs([]);
      setSelectedOperatingCompanyIds([]);
      setExpandedGeoNodeIds(new Set());

      const today = startOfDay(new Date());
      const wm = wmId
        ? allWeatherModels.find((w) => w.id === wmId)
        : undefined;
      const wmMaxDate = wm?.max_forecast_date
        ? startOfDay(parseISO(wm.max_forecast_date))
        : undefined;
      if (!wmMaxDate || !isValid(wmMaxDate)) {
        setSelectedDate(today);
      } else {
        setSelectedDate(isAfter(wmMaxDate, today) ? today : wmMaxDate);
      }

      setSelectedRun(initialRun);
      setExportFormat("excel");
      setInlineAlert(null);
      reset();
    }
  }, [open, stormImpactModels, reset]);

  // ============================================================================
  // Show error toast
  // ============================================================================
  useEffect(() => {
    if (error) {
      toast.error("Export failed", {
        description: error.message || "An unexpected error occurred.",
      });
    }
  }, [error]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleWeatherModelSelect = useCallback(
    (weatherModelId: string) => {
      setWeatherModelPopoverOpen(false);
      if (weatherModelId === selectedWeatherModelId) return;
      setSelectedWeatherModelId(weatherModelId);

      const compatible = getCompatibleImpactModels(stormImpactModels, weatherModelId);
      const compatibleIds = new Set(compatible.map((m) => m.model_id));
      const removedIds = selectedImpactModelIds.filter(
        (id) => !compatibleIds.has(id),
      );
      const kept = selectedImpactModelIds.filter((id) => compatibleIds.has(id));
      setSelectedImpactModelIds(kept);

      if (removedIds.length > 0) {
        const wmName =
          allWeatherModels.find((w) => w.id === weatherModelId)?.name ??
          "selected weather model";
        const removedNames = removedIds
          .map(
            (id) =>
              stormImpactModels.find((m) => m.model_id === id)?.model_name ??
              id,
          )
          .join(", ");
        showInlineAlert(
          `${removedNames} ${removedIds.length === 1 ? "is" : "are"} not available for ${wmName} and ${removedIds.length === 1 ? "has" : "have"} been deselected.`,
        );
      }

      setSelectedRun(undefined);
    },
    [selectedWeatherModelId, selectedImpactModelIds, stormImpactModels, allWeatherModels],
  );

  const handleImpactModelToggle = useCallback(
    (impactModelId: string) => {
      setSelectedImpactModelIds((prev) => {
        if (prev.includes(impactModelId)) {
          return prev.filter((id) => id !== impactModelId);
        }
        return [...prev, impactModelId];
      });
    },
    [],
  );

  const handleVariableToggle = useCallback((variableSlug: string) => {
    setSelectedVariableSlugs((prev) => {
      if (prev.includes(variableSlug)) {
        return prev.filter((s) => s !== variableSlug);
      }
      return [...prev, variableSlug];
    });
  }, []);

  const handleOperatingCompanyToggle = useCallback(
    (nodeId: string) => {
      const directChildren = childIdsMap.get(nodeId) ?? [];
      const idsToToggle = [nodeId, ...directChildren];

      setSelectedOperatingCompanyIds((prev) => {
        if (prev.includes(nodeId)) {
          const toRemove = new Set(idsToToggle);
          let current = nodeId;
          while (parentIdMap.has(current)) {
            toRemove.add(parentIdMap.get(current)!);
            current = parentIdMap.get(current)!;
          }
          return prev.filter((id) => !toRemove.has(id));
        }

        const next = new Set([...prev, ...idsToToggle]);

        // Remove previously-selected grandchildren+ of the clicked node so
        // that selecting a higher-level node (e.g. state) collapses the
        // selection down to its direct children only.
        const directChildSet = new Set(directChildren);
        for (const desc of descendantIdsMap.get(nodeId) ?? []) {
          if (!directChildSet.has(desc)) {
            next.delete(desc);
          }
        }

        return Array.from(next);
      });
    },
    [childIdsMap, parentIdMap, descendantIdsMap],
  );

  const handleGeoNodeExpandToggle = useCallback((nodeId: string) => {
    setExpandedGeoNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const handleExport = useCallback(() => {
    if (!selectedWeatherModelId) {
      toast.warning("Please select a weather model.");
      return;
    }
    if (selectedImpactModelIds.length === 0) {
      toast.warning("Please select at least one impact model.");
      return;
    }
    if (selectedVariableSlugs.length === 0) {
      toast.warning("Please select at least one variable to export.");
      return;
    }

    // Send only the deepest-selected-level IDs: keep a selected node only if
    // none of its direct children are also selected (i.e. it's the leaf of the
    // selection tree). When empty, send undefined so the backend defaults to OC level.
    const selectedSet = new Set(selectedOperatingCompanyIds);
    const effectiveGeoNodeIds = selectedOperatingCompanyIds.filter((id) => {
      const children = childIdsMap.get(id) ?? [];
      return children.length === 0 || !children.some((c) => selectedSet.has(c));
    });

    const request: ForecastExportRequest = {
      weatherModelId: selectedWeatherModelId,
      stormImpactModelIds: selectedImpactModelIds,
      variableSlugs: selectedVariableSlugs,
      format: exportFormat,
      forecastDate: format(selectedDate, "yyyy-MM-dd"),
      run: selectedRun ? selectedRun.toISOString() : undefined,
      geographicNodeIds:
        effectiveGeoNodeIds.length > 0 ? effectiveGeoNodeIds : undefined,
      stormId,
      trackSnapshotId,
    };

    trackRumAction?.("export_forecasts", {
      format: exportFormat,
      impactModelCount: selectedImpactModelIds.length,
      variableCount: selectedVariableSlugs.length,
    });

    exportForecasts(request, {
      onSuccess: () => {
        toast.success("Export downloaded successfully!");
      },
    });
  }, [
    selectedWeatherModelId,
    selectedImpactModelIds,
    selectedVariableSlugs,
    exportFormat,
    selectedDate,
    selectedRun,
    selectedOperatingCompanyIds,
    childIdsMap,
    stormId,
    trackSnapshotId,
    exportForecasts,
  ]);

  // Get organization name from hierarchy
  const organizationName = useMemo(() => {
    return geographicHierarchy?.hierarchy_name || "Organization";
  }, [geographicHierarchy]);

  // Button labels
  const weatherModelButtonLabel = useMemo(() => {
    if (!selectedWeatherModelId) return "Select weather model";
    const wm = allWeatherModels.find((w) => w.id === selectedWeatherModelId);
    return wm ? `${wm.name}${wm.acronym ? ` (${wm.acronym})` : ""}` : "";
  }, [selectedWeatherModelId, allWeatherModels]);

  const impactModelButtonLabel = useMemo(() => {
    if (selectedImpactModelIds.length === 0) return "Select impact model";
    if (selectedImpactModelIds.length === 1) {
      return (
        compatibleImpactModels.find(
          (m) => m.model_id === selectedImpactModelIds[0],
        )?.model_name ?? ""
      );
    }
    return `${selectedImpactModelIds.length} models selected`;
  }, [selectedImpactModelIds, compatibleImpactModels]);

  const variablesButtonLabel = useMemo(() => {
    if (selectedVariableSlugs.length === 0) return "Select variables";
    if (selectedVariableSlugs.length === 1) {
      return (
        availableVariables.find((v) => v.slug === selectedVariableSlugs[0])
          ?.name ?? ""
      );
    }
    return `${selectedVariableSlugs.length} selected`;
  }, [selectedVariableSlugs, availableVariables]);

  const hasModelsSelected = selectedImpactModelIds.length > 0 && !!selectedWeatherModelId;

  const noCommonRun =
    hasModelsSelected &&
    !isRunSelectorLoading &&
    availableRunsForSelector.length === 0;

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh]"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="pb-2">
          <DialogTitle>Export Forecast Data</DialogTitle>
          <DialogDescription>
            Configure your export by selecting models, operating companies,
            date, run, and variables.
          </DialogDescription>
        </DialogHeader>

        {inlineAlert && (
          <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300 animate-in fade-in slide-in-from-top-1 duration-200">
            <span className="shrink-0 mt-0.5 text-blue-500">&#9432;</span>
            <span className="flex-1">{inlineAlert}</span>
            <button
              type="button"
              className="shrink-0 ml-1 text-blue-400 hover:text-blue-600 dark:hover:text-blue-200"
              onClick={() => setInlineAlert(null)}
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        )}

        <div className="space-y-4">
            {/* Weather Model */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Weather Model</Label>
              <Popover open={weatherModelPopoverOpen} onOpenChange={setWeatherModelPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between overflow-hidden"
                  >
                    <span className="truncate">{weatherModelButtonLabel}</span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <div className="p-1">
                    <div
                      className="max-h-[300px] min-h-0 overflow-y-scroll overscroll-contain touch-pan-y"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      {allWeatherModels.map((wm) => (
                        <div
                          key={wm.id}
                          className={cn(
                            "flex items-center gap-2 px-2 py-2 text-sm rounded-sm cursor-pointer",
                            selectedWeatherModelId === wm.id
                              ? "bg-accent text-accent-foreground"
                              : "hover:bg-accent hover:text-accent-foreground",
                          )}
                          onClick={() => handleWeatherModelSelect(wm.id)}
                        >
                          <span>{wm.name}{wm.acronym ? ` (${wm.acronym})` : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Impact Model */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Impact Model</Label>
              <TooltipProvider delayDuration={300}>
                {!selectedWeatherModelId ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-block w-full">
                        <Button
                          variant="outline"
                          className="w-full justify-between"
                          disabled
                        >
                          <span className="truncate">Select impact model</span>
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      Select a weather model first.
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between overflow-hidden"
                      >
                        <span className="truncate">{impactModelButtonLabel}</span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                      <div className="p-1">
                        <div
                          className="max-h-[300px] min-h-0 overflow-y-scroll overscroll-contain touch-pan-y"
                          onWheel={(e) => e.stopPropagation()}
                        >
                          {compatibleImpactModels.map((model) => (
                            <label
                              key={model.model_id}
                              className="flex items-center gap-2 px-2 py-2 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
                            >
                              <Checkbox
                                checked={selectedImpactModelIds.includes(model.model_id)}
                                onCheckedChange={() => handleImpactModelToggle(model.model_id)}
                              />
                              <span>{model.model_name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </TooltipProvider>
            </div>

          {/* Variables (multi-select, intersection of selected IMs) */}
          <div className="space-y-2">
            <Label htmlFor="variables" className="text-sm font-medium">
              Variables
            </Label>
            <TooltipProvider delayDuration={300}>
              {selectedImpactModelIds.length === 0 ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block w-full">
                      <Button
                        variant="outline"
                        className="w-full justify-between"
                        disabled
                      >
                        <span className="truncate">Select variables</span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    Select an impact model first.
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between"
                      id="variables"
                    >
                      <span className="truncate">{variablesButtonLabel}</span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <div className="p-1">
                      <div
                        className="max-h-[300px] min-h-0 overflow-y-scroll overscroll-contain touch-pan-y"
                        onWheel={(e) => e.stopPropagation()}
                      >
                        {availableVariables.map((variable) => (
                          <label
                            key={variable.slug}
                            className="flex items-center gap-2 px-2 py-2 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedVariableSlugs.includes(
                                variable.slug,
                              )}
                              onCheckedChange={() =>
                                handleVariableToggle(variable.slug)
                              }
                            />
                            <span>
                              {variable.name}{" "}
                              {variable.units && `(${variable.units})`}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </TooltipProvider>
          </div>

          {/* Geographic Nodes */}
          <div className="space-y-2">
            <Label htmlFor="geographic-nodes" className="text-sm font-medium">
              Geographic nodes
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between bg-background border border-border hover:bg-accent"
                  id="geographic-nodes"
                >
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="h-4 w-4" />
                    <span className="truncate">{organizationName}</span>
                  </div>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <div className="p-1">
                  <div
                    className="max-h-[300px] min-h-0 overflow-y-scroll overscroll-contain touch-pan-y"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {!geographicHierarchy?.trees?.length ? (
                      <div className="px-2 py-4 text-sm text-center text-muted-foreground">
                        No geographic nodes found
                      </div>
                    ) : (
                      geographicHierarchy.trees.map((node) => (
                        <CollapsibleGeoNode
                          key={node.id}
                          node={node}
                          depth={0}
                          selectedIds={selectedIdsSet}
                          expandedIds={expandedGeoNodeIds}
                          disabledIds={disabledGeoNodeIds}
                          descendantIdsMap={descendantIdsMap}
                          onToggleSelect={handleOperatingCompanyToggle}
                          onToggleExpand={handleGeoNodeExpandToggle}
                        />
                      ))
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Forecast Date */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Forecast Date</Label>
            <TooltipProvider delayDuration={300}>
              <Popover>
                {!hasModelsSelected ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-block w-full">
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                            disabled
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {selectedDate
                              ? format(selectedDate, "PPP")
                              : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      Select models to enable date filtering.
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate
                        ? format(selectedDate, "PPP")
                        : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                )}
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      if (date) {
                        setSelectedDate(date);
                        setSelectedRun(undefined);
                      }
                    }}
                    defaultMonth={selectedDate}
                    disabled={(dateToCheck) => {
                      if (maxForecastDate) {
                        return dateToCheck > maxForecastDate;
                      }
                      return false;
                    }}
                  />
                </PopoverContent>
              </Popover>
            </TooltipProvider>
          </div>

          {/* Run */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Run</Label>
            <TooltipProvider delayDuration={300}>
              {!hasModelsSelected ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block w-full">
                      <div
                        className="bg-background border border-border rounded-md p-2 opacity-50 pointer-events-none [&_div[class*='bg-secondary']]:bg-transparent [&_div[class*='bg-secondary']]:text-foreground"
                        aria-disabled
                      >
                        <ModelRunSelector
                          selectedRunTime={
                            selectedRun?.toISOString() || null
                          }
                          onRunTimeSelect={(runTime) =>
                            setSelectedRun(new Date(runTime))
                          }
                          availableModelRuns={availableRunsForSelector}
                          weatherModelName="selected weather model"
                          isLoading={isRunSelectorLoading}
                          runHourMap={runHourMap}
                        />
                      </div>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    Select models to enable run selection.
                  </TooltipContent>
                </Tooltip>
              ) : noCommonRun ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                  No common run available across the selected impact models for this date.
                </div>
              ) : (
                <div className="bg-background border border-border rounded-md p-2 [&_div[class*='bg-secondary']]:bg-transparent [&_div[class*='bg-secondary']]:text-foreground [&_div[class*='bg-secondary']:hover]:bg-accent/50 [&_div[class*='bg-secondary']]:border-0 [&_div[class*='bg-secondary']]:px-0 [&_div[class*='bg-secondary']]:py-0">
                  <ModelRunSelector
                    selectedRunTime={selectedRun?.toISOString() || null}
                    onRunTimeSelect={(runTime) =>
                      setSelectedRun(new Date(runTime))
                    }
                    availableModelRuns={availableRunsForSelector}
                    weatherModelName={weatherModelButtonLabel}
                    isLoading={isRunSelectorLoading}
                    runHourMap={runHourMap}
                  />
                </div>
              )}
            </TooltipProvider>
          </div>

          {/* Format */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Format</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="exportFormat"
                  value="excel"
                  checked={exportFormat === "excel"}
                  onChange={() => setExportFormat("excel")}
                  className="accent-primary"
                />
                <span className="text-sm">Excel (.xlsx)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="exportFormat"
                  value="csv"
                  checked={exportFormat === "csv"}
                  onChange={() => setExportFormat("csv")}
                  className="accent-primary"
                />
                <span className="text-sm">CSV (.csv)</span>
              </label>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <div className="w-full flex justify-center">
            <Button
              onClick={handleExport}
              disabled={
                isExporting ||
                !selectedWeatherModelId ||
                selectedImpactModelIds.length === 0 ||
                selectedVariableSlugs.length === 0 ||
                noCommonRun
              }
              className="min-w-[120px]"
            >
              {isExporting ? (
                <>
                  <span
                    className="mr-2 inline-block h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin"
                    aria-hidden
                  />
                  <span>Exporting...</span>
                </>
              ) : (
                "Export"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
